/**
 * Session Configuration for Enhanced Persistence
 * 
 * This module defines session timeout and refresh settings for mobile-optimized
 * authentication, ensuring technicians stay logged in between stops.
 */

export const SESSION_CONFIG = {
  /**
   * JWT expiry time in seconds
   * 7 days = 604800 seconds
   * This must match the setting in your Supabase dashboard:
   * Authentication > Settings > JWT Expiry
   */
  JWT_EXPIRY_SECONDS: 604800, // 7 days
  
  /**
   * Refresh token expiry in seconds
   * 30 days = 2592000 seconds
   * Longer duration for "Remember Me" functionality
   */
  REFRESH_TOKEN_EXPIRY_SECONDS: 2592000, // 30 days
  
  /**
   * How often to check for token refresh needs (in milliseconds)
   * Check every 5 minutes = 300000 ms
   */
  REFRESH_CHECK_INTERVAL: 300000, // 5 minutes
  
  /**
   * Refresh token when it's within this many seconds of expiry
   * Refresh 1 hour before expiry = 3600 seconds
   */
  REFRESH_THRESHOLD_SECONDS: 3600, // 1 hour
  
  /**
   * Local storage key for "Remember Me" preference
   */
  REMEMBER_ME_KEY: 'aquivis_remember_me',
  
  /**
   * Local storage key for device fingerprint
   */
  DEVICE_FINGERPRINT_KEY: 'aquivis_device_id',
  
  /**
   * Session storage key for biometric prompt flag
   */
  BIOMETRIC_PROMPT_KEY: 'aquivis_biometric_pending',
} as const;

/**
 * Checks if session is about to expire and needs refresh
 */
export function shouldRefreshSession(expiresAt: number): boolean {
  const now = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = expiresAt - now;
  return timeUntilExpiry < SESSION_CONFIG.REFRESH_THRESHOLD_SECONDS;
}

/**
 * Checks if user has "Remember Me" enabled
 */
export function isRememberMeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SESSION_CONFIG.REMEMBER_ME_KEY) === 'true';
}

/**
 * Sets "Remember Me" preference
 */
export function setRememberMe(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  if (enabled) {
    localStorage.setItem(SESSION_CONFIG.REMEMBER_ME_KEY, 'true');
  } else {
    localStorage.removeItem(SESSION_CONFIG.REMEMBER_ME_KEY);
  }
}

/**
 * Generates a unique device fingerprint for trusted device tracking
 */
export async function getDeviceFingerprint(): Promise<string> {
  if (typeof window === 'undefined') return '';
  
  // Check if we already have a fingerprint
  const existing = localStorage.getItem(SESSION_CONFIG.DEVICE_FINGERPRINT_KEY);
  if (existing) return existing;
  
  // Generate new fingerprint from browser characteristics
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.maxTouchPoints || 0,
  ];
  
  const fingerprint = await hashString(components.join('|'));
  localStorage.setItem(SESSION_CONFIG.DEVICE_FINGERPRINT_KEY, fingerprint);
  
  return fingerprint;
}

/**
 * Simple hash function for device fingerprinting
 */
async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
